import { useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import {
  CalendarDays, FileSpreadsheet, FileText, Loader2, ArrowDownLeft, ArrowUpRight, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { formatMoney, isoDate } from "@/lib/money";

const IN_COLOR  = "hsl(var(--status-present))";
const OUT_COLOR = "hsl(var(--status-absent))";

function firstDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function DatePickerBtn({ value, onChange, testid, label }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid={testid} className="h-10 justify-start rounded-md gap-2 w-[180px]">
            <CalendarDays className="h-4 w-4" />
            <span className="font-mono-mini tabular-nums text-xs">{format(value, "MMM d, yyyy")}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-auto">
          <Calendar mode="single" selected={value} onSelect={(d) => { if (d) { onChange(d); setOpen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function PettyCashReportsPage() {
  const [startDate, setStartDate] = useState(firstDayOfMonth());
  const [endDate, setEndDate] = useState(new Date());
  const [kind, setKind] = useState("all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const run = async () => {
    if (endDate < startDate) { toast.error("End date must be on or after start date"); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: isoDate(startDate), end_date: isoDate(endDate) });
      if (kind !== "all") params.set("kind", kind);
      const { data } = await api.get(`/petty-cash/report?${params.toString()}`);
      setReport(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const download = async (fmt) => {
    setExporting(fmt);
    try {
      const params = new URLSearchParams({ start_date: isoDate(startDate), end_date: isoDate(endDate), format: fmt });
      if (kind !== "all") params.set("kind", kind);
      const res = await fetch(`${API}/petty-cash/export?${params.toString()}`, { credentials: "include" });
      if (!res.ok) {
        let msg = "Export failed";
        try {
          const j = await res.json();
          msg = j.detail || msg;
        } catch (parseErr) {
          // Response body wasn't JSON — keep the generic error message.
          console.debug("Export error body was not JSON:", parseErr?.message || parseErr);
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `petty_cash_${isoDate(startDate)}_to_${isoDate(endDate)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()} report`);
    } catch (e) { toast.error(e.message || "Export failed"); }
    finally { setExporting(null); }
  };

  return (
    <div className="p-8 lg:p-10" data-testid="pc-reports-page">
      <div>
        <div className="font-mono-mini text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Reports</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Cash statement</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Pick a range, filter by type, and export as Excel or PDF.
        </p>
      </div>

      {/* Filters */}
      <Card className="mt-8 p-5 rounded-2xl border-border shadow-soft">
        <div className="flex flex-wrap items-end gap-4">
          <DatePickerBtn value={startDate} onChange={setStartDate} testid="pc-report-start" label="From" />
          <DatePickerBtn value={endDate}   onChange={setEndDate}   testid="pc-report-end"   label="To" />
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
            <ToggleGroup
              type="single"
              value={kind}
              onValueChange={(v) => { if (v) setKind(v); }}
              className="border border-border rounded-md overflow-hidden h-10"
            >
              <ToggleGroupItem value="all" data-testid="pc-report-kind-all" className="h-10 px-3 text-xs rounded-none border-0 data-[state=on]:bg-foreground data-[state=on]:text-background">All</ToggleGroupItem>
              <ToggleGroupItem value="in"  data-testid="pc-report-kind-in"  className="h-10 px-3 text-xs rounded-none border-0 data-[state=on]:bg-[hsl(var(--status-present))] data-[state=on]:text-white">In</ToggleGroupItem>
              <ToggleGroupItem value="out" data-testid="pc-report-kind-out" className="h-10 px-3 text-xs rounded-none border-0 data-[state=on]:bg-[hsl(var(--status-absent))] data-[state=on]:text-white">Out</ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="flex-1" />

          <div className="flex flex-wrap gap-2">
            <Button onClick={run} disabled={loading} className="h-10 btn-lift" data-testid="pc-run-report-btn">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Run report
            </Button>
            <Button variant="outline" onClick={() => download("xlsx")} disabled={!report || exporting !== null} className="h-10 btn-lift" data-testid="pc-export-xlsx-btn">
              {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              Excel
            </Button>
            <Button variant="outline" onClick={() => download("pdf")} disabled={!report || exporting !== null} className="h-10 btn-lift" data-testid="pc-export-pdf-btn">
              {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              PDF
            </Button>
          </div>
        </div>
      </Card>

      {/* Totals */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Card className="p-5 rounded-2xl border-border shadow-soft" data-testid="pc-report-total-in">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <ArrowDownLeft className="h-3.5 w-3.5" style={{ color: IN_COLOR }} /> Total in
            </div>
            <div className="font-display text-3xl font-bold tabular-nums mt-3" style={{ color: IN_COLOR }}>{formatMoney(report.total_in)}</div>
          </Card>
          <Card className="p-5 rounded-2xl border-border shadow-soft" data-testid="pc-report-total-out">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <ArrowUpRight className="h-3.5 w-3.5" style={{ color: OUT_COLOR }} /> Total out
            </div>
            <div className="font-display text-3xl font-bold tabular-nums mt-3" style={{ color: OUT_COLOR }}>{formatMoney(report.total_out)}</div>
          </Card>
          <Card className="p-5 rounded-2xl border-border shadow-soft" data-testid="pc-report-net">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <TrendingUp className="h-3.5 w-3.5" /> Net
            </div>
            <div
              className="font-display text-3xl font-bold tabular-nums mt-3"
              style={{ color: report.net < 0 ? OUT_COLOR : IN_COLOR }}
            >{formatMoney(report.net)}</div>
          </Card>
        </div>
      )}

      {/* Head breakdown */}
      {report && report.head_breakdown.length > 0 && (
        <Card className="mt-6 p-0 rounded-2xl border-border shadow-soft overflow-hidden">
          <div className="px-5 py-3 border-b border-border text-sm font-medium">By head</div>
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead className="w-[70px]">Type</TableHead>
                <TableHead>Head</TableHead>
                <TableHead className="text-right w-[100px]">Count</TableHead>
                <TableHead className="text-right w-[160px]">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.head_breakdown.map((h) => (
                <TableRow key={h.head_id} data-testid={`pc-head-breakdown-${h.head_id}`}>
                  <TableCell>
                    <span
                      className="text-[10px] font-mono-mini px-1.5 py-0.5 rounded-sm text-white"
                      style={{ background: h.kind === "in" ? IN_COLOR : OUT_COLOR }}
                    >
                      {h.kind.toUpperCase()}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{h.head_name}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{h.count}</TableCell>
                  <TableCell
                    className="text-right font-mono-mini tabular-nums text-sm font-medium"
                    style={{ color: h.kind === "in" ? IN_COLOR : OUT_COLOR }}
                  >
                    {formatMoney(h.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Transactions table */}
      <Card className="mt-6 p-0 rounded-2xl border-border shadow-soft overflow-hidden">
        <div className="px-5 py-3 border-b border-border text-sm font-medium">
          Transactions {report ? `(${report.transactions.length})` : ""}
        </div>
        {loading && !report ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : !report || report.transactions.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No transactions in this range.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead className="w-[110px]">Date</TableHead>
                <TableHead className="w-[70px]">Type</TableHead>
                <TableHead className="w-[120px]">Voucher #</TableHead>
                <TableHead>Head</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right w-[140px]">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.transactions.map((t) => (
                <TableRow key={t.tx_id} data-testid={`pc-report-tx-${t.tx_id}`}>
                  <TableCell className="font-mono-mini tabular-nums text-xs">{t.date}</TableCell>
                  <TableCell>
                    <span
                      className="text-[10px] font-mono-mini px-1.5 py-0.5 rounded-sm text-white"
                      style={{ background: t.kind === "in" ? IN_COLOR : OUT_COLOR }}
                    >
                      {t.kind === "in" ? "IN" : "OUT"}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono-mini text-xs text-muted-foreground">
                    {t.voucher_no || "—"}
                  </TableCell>
                  <TableCell className="text-sm">{t.head_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md truncate">{t.note || "—"}</TableCell>
                  <TableCell
                    className="text-right font-mono-mini tabular-nums text-sm font-medium"
                    style={{ color: t.kind === "in" ? IN_COLOR : OUT_COLOR }}
                  >
                    {t.kind === "in" ? "+" : "−"} {formatMoney(t.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
